import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { cn } from "@/modules/lib/utils"
import { ProgramCalendarEvent } from "@/modules/programs/components/programSession/programCalendarEvent"
import { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import type { BlockSessionInputDto } from "@/modules/sessions/gateway/sessionBlockDto"
import { useProgramSessionActions } from "@/modules/sessions/hooks/useProgramSessionActions"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import { useTemplateExerciseImportSidebar } from "@/modules/template/hooks/useTemplateSidebar"
import { defaultDateFormat } from "@/modules/utils/dateUtils"
import { Calendar } from "@modules/calendar/calendar"
import { type EventType } from "@modules/calendar/calendarTypes"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { type DetailedSessionType } from "@modules/sessions/sessionsType"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useProgramSessionEvents } from "@modules/training/hooks/useTraining"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

type ProgramCalendarProps = {
  programId: string
  calendarRange:
    | { startDate: number | undefined; endDate: number | undefined }
    | { startDate: string; endDate: string }
    | undefined
  loading?: boolean
  className?: string
} & React.ComponentProps<"div">

export function ProgramCalendar({ programId, calendarRange, loading, className, ...props }: ProgramCalendarProps) {
  const [eventUpdating, setEventUpdating] = useState<EventType>()
  const [openedSessionId, setOpenedSessionId] = useState<string>()

  const { data: program } = useProgram(programId)
  const { navigateToProgramView } = useProgramSidebar()
  const { programInfiniteSessionApi } = useServicesContext()
  const { updateSession, addBlockMutation, updateBlockMutation } = useProgramSessionActions()
  const { refreshCalendarEvents, draggingEvent, setDraggingEvent } = useCalendar()
  const { hideSidebar, isSidebarVisible } = useSidebar()
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const {
    navigateToSingleBlockFormEdition,
    navigateToSingleBlockFormCreation,
    navigateToSessionView,
    navigateToSessionForm,
  } = useSessionSidebar()
  const { navigateToTemplateExerciseImportView } = useTemplateExerciseImportSidebar()

  const prevIsSidebarVisible = useRef<boolean>(isSidebarVisible)

  const canUserSeeSessions =
    isAuthor ||
    isAcceptedSharedCoach ||
    program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
    program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ||
    program.subscriptionStatus === ProgramSubscriptionStatus.TRIALING ||
    program.subscriptionStatus === ProgramSubscriptionStatus.TRIAL_PENDING_CANCELLATION

  const {
    data: { events: programSessionEvents, sessions: programSessions },
  } = useProgramSessionEvents({
    programId: canUserSeeSessions ? program.id : "",
    programType: UserProgramDetailsEntity.isSessionsProgramType(program.type) ? program.type : undefined,
    startDate: calendarRange?.startDate,
    endDate: calendarRange?.endDate,
  })

  const onEventDragStop = async (dateDest?: dayjs.Dayjs, position?: number) => {
    if (draggingEvent) {
      const shouldApplyChange =
        "date" in draggingEvent &&
        "virtualProgramByDateIndex" in draggingEvent &&
        ((dateDest && !dateDest.isSame(draggingEvent.date)) || draggingEvent.virtualProgramByDateIndex !== position)
      if (shouldApplyChange && (program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO)) {
        try {
          setEventUpdating(draggingEvent)
          const session = ProgramInfiniteSessionEntity.fromDto(
            await programInfiniteSessionApi.getDetailedSession(draggingEvent.id),
          )

          if (session instanceof ProgramInfiniteSessionEntity && dateDest) {
            await updateSession({
              id: draggingEvent.id,
              programId: program.id,
              programType: program.type,
              session: {
                ...ProgramInfiniteSessionEntity.fromEntityToInputDto(session),
                date: dayjs(dateDest).format(defaultDateFormat),
                virtualProgramByDateIndex: position ?? session.virtualProgramByDateIndex ?? 0,
              },
            })
          }
        } catch (e: unknown) {
          toast.error("Error", { description: (e as Error).message, classNames: errorToastClassName() })
        } finally {
          refreshCalendarEvents()
          setEventUpdating(undefined)
          setDraggingEvent(undefined)
        }
      }
    }
  }

  const onViewDetailsProgramClick = () => {
    navigateToProgramView(program.id, true)
  }

  const onClickOnSlot = async (slot: DetailedSessionType) => {
    const result = await navigateToSessionView(slot, refreshCalendarEvents, () => {
      setOpenedSessionId(undefined)
      return Promise.resolve(true)
    })
    if (result) setOpenedSessionId(slot.id)
  }

  const onCreateBlockClick = (
    session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity,
    newBlockType: SessionBlockType,
  ) => {
    setOpenedSessionId(session.id)
    navigateToSingleBlockFormCreation(session.blocks.length, newBlockType, async (newBlock: BlockSessionInputDto) => {
      await addBlockMutation.mutateAsync({
        programId: program.id,
        session: session,
        block: newBlock,
      })

      hideSidebar(false)
      refreshCalendarEvents()
    })
  }

  const onImportTemplateClick = (session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity) => {
    setOpenedSessionId(session.id)
    const updatedBlockIndex = session.blocks.length
    navigateToTemplateExerciseImportView(updatedBlockIndex, async (exercise: ExerciseFormikType) => {
      const newBlock = {
        type: SessionBlockType.SESSION_EXERCISE,
        content: SessionBlockExerciseEntity.fromFormikToInputDto(exercise, updatedBlockIndex),
      }

      await addBlockMutation.mutateAsync({
        programId: program.id,
        session: session,
        block: newBlock,
      })

      hideSidebar(false)
    })
  }

  const onBlockEditClick = (session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity, orderIndex: number) => {
    // Handle block edition on parent component because the drag and drop doesn't work properly if this handle is in the block component
    const block = session.blocks[orderIndex]
    if (!block) return
    setOpenedSessionId(session.id)
    navigateToSingleBlockFormEdition(block, orderIndex, async (updatedBlock: BlockSessionInputDto) => {
      await updateBlockMutation.mutateAsync({
        sessionId: session.id,
        block: updatedBlock,
      })
      hideSidebar(false)
      refreshCalendarEvents()
    })
  }

  const onAddEventClick = async (value?: Date | number) => {
    if (typeof value === "number") {
      await navigateToSessionForm(program, undefined, value, undefined, {
        refreshCalendarEvents: refreshCalendarEvents,
      })
    } else if (value) {
      await navigateToSessionForm(program, undefined, dayjs(value).format(defaultDateFormat), undefined, {
        refreshCalendarEvents: refreshCalendarEvents,
      })
    }
  }

  useEffect(() => {
    // Only clear openedSession when sidebar transitions from visible to hidden
    if (prevIsSidebarVisible.current && !isSidebarVisible) {
      setOpenedSessionId(undefined)
    }
    // Update the ref to track current value for next render
    prevIsSidebarVisible.current = isSidebarVisible
  }, [isSidebarVisible])

  const noAccessText = useMemo(() => {
    return word(
      program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_REQUEST
        ? "program.session.noAccessUntilAccepted"
        : program.type === ProgramType.ONOFF
          ? "program.onOff.session.noAccessUntilBought"
          : "program.session.noAccessUntilSubscribed",
    )
  }, [program])

  const noAccessButtonProps = useMemo(() => {
    return program.subscriptionStatus !== ProgramSubscriptionStatus.PENDING_REQUEST
      ? {
          text:
            program.type === ProgramType.ONOFF
              ? word("program.onOff.subscription.buy.free.title")
              : word("program.subscription.subscribe"),
          onClick: onViewDetailsProgramClick,
        }
      : undefined
  }, [program, onViewDetailsProgramClick])

  const renderEvent = useCallback(
    (index: number, event: EventType, displayDetailedEvents?: boolean) => {
      const session = programSessions.find((session) => session.id === event.id)
      const programSessionEvent = programSessionEvents.find((sessionEvent) => sessionEvent.id === event.id)
      if (session && programSessionEvent) {
        return (
          <ProgramCalendarEvent
            key={session.id}
            index={index}
            event={programSessionEvent}
            session={session}
            displayDetailedEvents={displayDetailedEvents}
            onEventClick={onClickOnSlot}
            onBlockEditClick={onBlockEditClick}
            onCreateBlockClick={onCreateBlockClick}
            onImportTemplateClick={onImportTemplateClick}
            isUpdating={eventUpdating?.id === event.id}
            isDetailsOpen={openedSessionId === event.id}
            programId={program.id}
          />
        )
      }
      return null
    },
    [
      program,
      programSessions,
      programSessionEvents,
      onClickOnSlot,
      onBlockEditClick,
      onCreateBlockClick,
      eventUpdating,
      openedSessionId,
    ],
  )

  return (
    <div className={cn("mb-8 flex grow flex-col content-center", className)} {...props}>
      <Calendar
        calendarEvents={programSessionEvents}
        showNoAccessOverlay={!canUserSeeSessions}
        loading={loading}
        noAccessText={noAccessText}
        noAccessButton={noAccessButtonProps}
        canAddEvents={isAuthor || isAcceptedSharedCoach}
        onAddEventClick={(day) => void onAddEventClick(day)}
        eventLimitPerDay={program.type === ProgramType.ONOFF || program.type === ProgramType.PRO ? 1 : undefined}
        isDragAndDropActive={
          program.type !== ProgramType.ONOFF && program.type !== ProgramType.PRO && (isAuthor || isAcceptedSharedCoach)
        }
        onEventDragStop={onEventDragStop}
        customRenderEvent={renderEvent}
      />
    </div>
  )
}
